<template>
  <q-dialog v-model="shown">
    <q-card
      style="min-width: 70%; display: flex; flex-direction: column;"
      :style="{ 'max-height': $q.screen.gt.sm ? '80vh' : '100vh' }"
      :loading="loading"
    >
      <q-inner-loading
        style="z-index: 1000"
        :showing="loading"
      >
        <q-spinner size="xl" />
        <div v-if="linkingSiteTotal !== 0 && !$_.isEmpty(linkingSiteName)">
          Linking to {{ linkingSiteName }} ({{ linkingSiteIndex }}/{{ linkingSiteTotal }})
        </div>
      </q-inner-loading>
      <q-card-section class="row items-center q-mx-xs flex-shrink-0">
        <div class="text-h6">
          Link To Sites
        </div>
        <q-space />
        <q-btn
          v-if="!$_.isEmpty(sitesWithoutLibraryChildren)"
          class="gt-xs"
          :label="selectedAll ? 'Deselect All' : 'Select All'"
          :disable="!readyToLink"
          @click="selectAll()"
        />
        <q-btn
          v-close-popup
          icon="close"
          flat
          round
          dense
        />
      </q-card-section>
      <q-card-section class="flex-grow-1 overflow-auto">
        <q-expansion-item
          v-for="(circuitSites, circuit) in circuitsWithSites"
          :key="circuit"
          class="full-width"
          :disable="$_.keys(circuitsWithSites).length === 1"
          :default-opened="$_.keys(circuitsWithSites).length === 1"
          expand-separator
          popup
        >
          <template #header>
            <q-item-section
              class="col"
            >
              <div class="text-h7">
                <!-- If we are on the Library circuit, circuitsWithSites will include circuit as name -->
                <!-- If we are on a normal circuit, circuitsWithSites circuit is null -->
                {{ $_.keys(circuitsWithSites).length === 1 ? currentCircuit.name : circuit }}
                <q-badge
                  :color="circuitBadgeColor(circuitSites)"
                  floating
                >
                  {{ `${alreadyLinkedCount(circuitSites)}/${circuitSites.length}` }}
                </q-badge>
              </div>
            </q-item-section>
            <q-space />
            <q-btn
              v-if="!$_.isEmpty(unlinkedSites(circuitSites))"
              class="gt-xs"
              :label="selectedAllSites(unlinkedSites(circuitSites)) ? 'Deselect All' : 'Select All'"
              :disable="!readyToLink"
              @click="selectAll(circuitSites)"
            />
          </template>
          <q-list
            bordered
            separator
          >
            <q-item
              v-for="site in $_.sortBy(circuitSites, site => site.isLibrary ? '0' : site.label)"
              :key="site.id"
              :class="{ 'bg-grey-3': alreadyLinked(site) }"
              :disable="!readyToLink"
              clickable
              @click="toggle(site)"
            >
              <q-item-section>
                <q-checkbox
                  :model-value="alreadyLinked(site) || isSelected(site)"
                  style="pointer-events: none;"
                  :label="site.label"
                  :disabled="!readyToLink || alreadyLinked(site)"
                />
              </q-item-section>
            </q-item>
          </q-list>
        </q-expansion-item>
      </q-card-section>
      <q-card-actions class="row justify-end q-ma-sm flex-shrink-0">
        <q-btn
          :label="linkLabel"
          color="primary"
          :disable="$_.isEmpty(selectedSiteIds)"
          :loading="loading"
          @click="linkModelToSites"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script>

import { Notify } from 'quasar'

export default {
  name: 'LinkToSitesDialog',
  configuration: ['currentCircuit'],
  props: {
    modelValue: {
      type: Boolean,
      required: false
    },
    circuitsWithSites: {
      type: Object,
      required: true
    },
    model: {
      type: Object,
      required: false,
      default: null
    }
  },
  emits: ['update:model-value'],
  data () {
    return {
      shown: false,
      selectedSitesById: {},
      loading: false,
      linkingSiteName: '',
      linkingSiteIndex: 0,
      linkingSiteTotal: 0
    }
  },
  computed: {
    linkLabel () {
      return 'Link to Sites'
    },
    sites () {
      return _.flatten(_.values(this.circuitsWithSites))
    },
    sitesWithoutLibraryChildren () {
      return _.filter(this.sites, (site) => !_.includes(this.model.libraryChildSiteIds, site.id))
    },
    selectedSiteIds () {
      return _.keys(this.selectedSitesById)
    },
    selectedAll () {
      return this.selectedSiteIds.length === this.sitesWithoutLibraryChildren.length
    },
    readyToLink () {
      return !this.model.loading && !this.model.dirty && !this.loading
    }
  },
  watch: {
    modelValue () {
      this.shown = this.modelValue
    },
    shown () {
      this.$emit('update:model-value', this.shown)
    }
  },
  methods: {
    show () {
      this.shown = true
    },
    isSelected (site) {
      return !_.isNil(this.selectedSitesById[site.id])
    },
    toggle (site) {
      if (this.isSelected(site)) {
        _.unset(this.selectedSitesById, site.id)
      } else {
        this.selectedSitesById[site.id] = site
      }
    },
    selectAll (sites = this.sitesWithoutLibraryChildren) {
      const unlinkedSites = this.unlinkedSites(sites)

      if (this.selectedAllSites(unlinkedSites)) {
        _.each(unlinkedSites, (site) => {
          delete this.selectedSitesById[site.id]
        })
      } else {
        _.each(unlinkedSites, (site) => {
          this.selectedSitesById[site.id] = site
        })
      }
    },
    selectedAllSites (sites) {
      return _.isEmpty(_.difference(_.map(sites, site => `${site.id}`), this.selectedSiteIds))
    },
    alreadyLinked (site) {
      return _.includes(this.model.libraryChildSiteIds, site.id)
    },
    alreadyLinkedCount (sites) {
      return _.sumBy(sites, site => this.alreadyLinked(site) || this.isSelected(site) ? 1 : 0)
    },
    unlinkedSites (sites) {
      return _.filter(sites, site => !this.alreadyLinked(site))
    },
    circuitBadgeColor (sites) {
      const librarySite = _.find(sites, 'isLibrary')

      // If there is no library site, use neutral color
      if (!librarySite) return 'grey'

      // If there is a library site and it is in use, return positive color
      if (this.isSelected(librarySite) || this.alreadyLinked(librarySite)) return 'positive'

      // If there is a library site and it is not in use, return warning color
      // This means linked sites are not using their circuit library for model links, and are linking directly
      return 'warning'
    },
    async linkModelToSites () {
      if (_.isEmpty(this.selectedSiteIds)) return

      const model = this.model
      const siteIds = this.selectedSiteIds

      this.linkingSiteName = ''
      this.linkingSiteIndex = 0
      this.linkingSiteTotal = siteIds.length

      try {
        this.loading = true

        const allErrors = []
        // We cannot use await within _.each
        for (const siteId of siteIds) {
          this.linkingSiteName = this.selectedSitesById[siteId].label
          this.linkingSiteIndex++
          const { errors: duplicationErrors } = await model.duplicateModelToSite(model.id, siteId)
          allErrors.push(duplicationErrors)
        }

        this.linkingSiteName = ''
        this.linkingSiteIndex = 0
        this.linkingSiteTotal = 0
        this.loading = false

        if (!_.isEmpty(_.flatten(allErrors))) {
          Notify.create({
            message: 'Error linking model to sites',
            type: 'negative',
            position: 'top'
          })
        } else {
          this.shown = false
        }
      } finally {
        this.linkingSiteName = ''
        this.linkingSiteIndex = 0
        this.linkingSiteTotal = 0
        this.loading = false
        model.fetch(true)
      }
    }
  }
}

</script>

<style lang="scss" scoped>
.q-card {
  .q-card-section {
    &.flex-grow-1 {
      flex: 1 1 auto;
    }
    &.flex-shrink-0 {
      flex: 0 0 auto;
    }
  }
}
</style>
